import type { ReactElement } from 'react';
import type { GetStaticProps, GetStaticPropsContext, GetStaticPaths, GetStaticPathsContext } from 'next';
import type { CategoryViewProps, GameRequestProps, CategoryProps, CategoryRequestProps } from '../../types';

import * as React from 'react';

import { View } from '../../generated/brand';
import { BaseLayout } from '../../layouts/BaseLayout';

import { NextSeo, CollectionPageJsonLd, BreadcrumbJsonLd } from 'next-seo';
import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';
import { useAppContext } from '../../contexts/AppContext';
import { getLocalizedPaths } from '../../utils/localize-helpers';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import { getAllCollections, getCollection, getLatestGames } from '../../app/api';
import { mappingCollection, mappingGamesMinimal } from '../../app/mappings';
import { CollectionTypesEnum } from '../../types/enum';
import { translateUrl } from 'next-translate-routes';
import { getNonPageNamespaces } from '../../utils/get-non-page-namespaces';

export default function DynamicPage(props: CategoryViewProps) {
  const { title, collection } = props;
  const { t, i18n } = useTranslation(['common', 'brand']);
  const appContext = useAppContext();
  const { name } = appContext.useConfig();
  const router = useRouter();
  const paths = getLocalizedPaths(router);
  const pageContextWithPaths = { ...props.pageContext, paths };
  const seoTitle = i18n.exists('brand:title', { fallbackLng: props.pageContext.locale })
    ? t('brand:title', { title })
    : t('common:title', { title });

  return (
    <>
      <NextSeo
        title={seoTitle}
        description={
          i18n.exists('brand:description', { fallbackLng: props.pageContext.locale })
            ? t('brand:description', { title, description: collection.description })
            : t('common:description', { title, description: collection.description })
        }
        openGraph={{
          images: [
            {
              url: `${process.env.PROJECT_ASSET_ABSOLUTE_URL}${
                collection.games[0]?.images?.cover ||
                collection.games[0]?.images?.cover1x1 ||
                collection.images.cover1x1 ||
                collection.images.cover
              }/?ar[r]=1x1&ar[w]=512&fmt=.png`,
              width: 512,
              height: 512,
              alt: `${seoTitle}`,
              type: 'image/png',
            },
          ],
        }}
      />
      <CollectionPageJsonLd
        name={title || seoTitle}
        hasPart={collection.games.map((game) => ({
          about: game.description,
          author: { name },
          name: game.name,
          datePublished: game.publishedAt,
          audience: 'Internet',
          keywords: 'schema',
          thumbnailUrl: `${process.env.PROJECT_ASSET_ABSOLUTE_URL}${
            game.images.cover1x1 || game.images.cover
          }/?ar[r]=1x1&ar[w]=512&fmt=.png`,
          image: `${process.env.PROJECT_ASSET_ABSOLUTE_URL}${
            game.images.cover16x9 || game.images.cover1x1 || game.images.cover
          }/`,
        }))}
      />
      <BreadcrumbJsonLd
        itemListElements={pageContextWithPaths.breadcrumbs.map((crumb, index) => ({
          position: index + 1,
          name: crumb.name,
          item: crumb.href,
        }))}
      />
      <View {...props} pageContext={pageContextWithPaths} />
    </>
  );
}

// Adding getLayout property. Allowing us to return a React component for the layout.
// This allows us to define the layout on a per-page basis. Since we're returning a function,
// we can have complex nested layouts if desired.
DynamicPage.getLayout = function getLayout(page: ReactElement) {
  const { props } = page;
  const { pageContext } = props;
  return (
    <BaseLayout pageType={CollectionTypesEnum.BRAND} pageContext={pageContext}>
      {page}
    </BaseLayout>
  );
};

export const getStaticPaths: GetStaticPaths = async (context: GetStaticPathsContext) => {
  // Request all brands by locale for static pre-rendering.
  // Always make sure we return the object below.
  // https://nextjs.org/docs/advanced-features/i18n-routing#dynamic-routes-and-getstaticprops-pages
  const locales: (string | undefined)[] = context?.locales || [context.defaultLocale];
  const promises: Promise<CategoryRequestProps[] | undefined>[] = locales.map((locale) =>
    getAllCollections(
      CollectionTypesEnum.BRAND,
      {
        _limit: 6,
        _fieldsGame: 'image',
      },
      {
        ssr: true,
        locale,
      }
    )
  );
  const response: (CategoryRequestProps[] | undefined)[] = await Promise.all(promises);
  const localizedBatches: { id: string; slug: string; locale: string | undefined }[][] = Array.isArray(response)
    ? response.map((batch: CategoryRequestProps[] | undefined, index: number) => {
        return Array.isArray(batch)
          ? batch.map((brand: CategoryRequestProps) => {
              return {
                id: brand.id,
                slug: brand.code,
                locale: locales[index],
              };
            })
          : [];
      })
    : [];
  const brands = Array.prototype.concat.apply([], localizedBatches);

  return {
    paths: brands.map((path: CategoryProps) => ({
      params: {
        slug: path.slug,
      },
      locale: path.locale,
    })),
    // Use server side rendering (SSR) when a page is not cached in our Next file system.
    // Let the user or search bot wait until then; before continuing the route to the designated page.
    // https://nextjs.org/docs/api-reference/data-fetching/get-static-paths#fallback-true
    fallback: 'blocking',
  };
};

export const getStaticProps: GetStaticProps = async (context: GetStaticPropsContext) => {
  const url = `${process.env.PROJECT_URL?.replace(/\/+$/, '')}`;
  const slug: string = (context?.params?.slug || '') as string;
  const locale: string = (context.locale || context.defaultLocale) as string;

  const brand: CategoryRequestProps | undefined = await getCollection(
    slug,
    CollectionTypesEnum.BRAND,
    {
      _fieldsGame: 'image',
    },
    {
      ssr: true,
      locale,
    }
  );
  
    const popular: CategoryRequestProps | undefined = await getCollection(
    `${process.env.PROJECT_CODE}-popular`,
    CollectionTypesEnum.COLLECTION,
    {
      _fieldsGame: 'image',
    },
    {
      ssr: true,
      locale,
    }
  );

  const latest: GameRequestProps[] | undefined = await getLatestGames(
    {
      period: 'week',
      _limit: 12,
      _fieldsGame: 'image', // Include "image" property for each game.
    },
    {
      ssr: true,
      locale,
    }
  );

  if (!brand?.id) return { notFound: true };

  const page = {
    title: brand?.name || null,
    body: brand?.description || null,
    locale: context.locale,
  };
  const { title, body } = page;
  const pageContext = {
    locale: page.locale,
    locales: context.locales,
    defaultLocale: context.defaultLocale,
    // Todo: HUZ2ERA-977 - have the backend return translated slugs as part of the game object response.
    slugs:
      context?.locales?.map((locale) => ({
        locale,
        slug,
      })) || [],
    slug,
    breadcrumbs: [
      { href: url, path: '/', name: 'Home' },
      {
        href: `${url}${translateUrl('/brands', locale)}`,
        path: `${translateUrl('/brands', locale)}`,
        name: 'Brands', // Todo: HUZ2ERA-1112 - Translate crumb name on server side?
      },
      {
        href: `${url}${translateUrl(`/brand/${slug}`, locale)}`,
        path: `${translateUrl(`/brand/${slug}`, locale)}`,
        name: brand?.name || '',
      },
    ],
  };
  // const localizedPaths = getLocalizedPaths(pageContext);
  const global = {
    popular: popular && Array.isArray(popular?.games) ? [...mappingGamesMinimal(popular.games)] : [],
    latest: Array.isArray(latest) ? [...mappingGamesMinimal(latest)] : [],
  };

  return {
    props: {
      title,
      body,
      collection: {
        ...mappingCollection(brand),
      },
      global,
      pageContext,
      // Get our translations by namespace and locale.
      ...(await serverSideTranslations(locale as string, ['common', 'brand', ...getNonPageNamespaces(locale)])),
    },
    // Incremental Static Regeneration (ISR).
    // https://nextjs.org/docs/basic-features/data-fetching/incremental-static-regeneration
    revalidate: parseInt(process.env.PROJECT_SSR_REVALIDATE_SECONDS || '') || 60,
  };
};
